/**
 * One row of the connection form (Option C, C2, plan §3.4): a label and its control, label-left from a 31-rem form
 * content box and label-above below it (`formLayout.ts`). Every control keeps a `<label htmlFor>`; a row of several
 * controls (checkboxes, radios, swatches) names them as a group instead (`role="group"` + `aria-labelledby`). A row
 * with no label (a lone checkbox) keeps the empty label column so its control lines up with the fields above.
 *
 * `data-row` names the row for the section-order tests and the e2e geometry checks.
 */
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { EMPTY_LABEL_CELL_CLASS, LABEL_CELL_CLASS, LABEL_NOTE_CLASS, ROW_CLASS } from './formLayout';

type Props = {
  /** `data-row`: which row this is (the field name, or a descriptive id for a group). */
  row: string;
  label?: ReactNode;
  /** The control's id: the label is a `<label htmlFor>`. */
  htmlFor?: string;
  /** A group of controls: the label is the group's name (`<span id>` + `role="group"`). */
  groupId?: string;
  /** A muted note under (or after) the label, e.g. "optional". */
  note?: string;
  /** A muted line under the control. */
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
  testId?: string;
};

/** The id of a row's `<label>`: a button beside the control (Browse…, Browse Azure) is described by it. */
export const rowLabelId = (controlId: string) => `${controlId}-label`;

export function FormRow({ row, label, htmlFor, groupId, note, hint, children, className, testId }: Props) {
  const labelText = (
    <>
      {label}
      {note && <span className={LABEL_NOTE_CLASS}>{note}</span>}
    </>
  );
  const labelCell = label === undefined
    ? <span aria-hidden className={EMPTY_LABEL_CELL_CLASS} />
    : groupId
      ? <span id={groupId} className={LABEL_CELL_CLASS}>{labelText}</span>
      : <label htmlFor={htmlFor} id={htmlFor ? rowLabelId(htmlFor) : undefined} className={LABEL_CELL_CLASS}>{labelText}</label>;
  return (
    <div className={cn(ROW_CLASS, className)} data-row={row} data-testid={testId}>
      {labelCell}
      <div className="min-w-0" {...(groupId ? { role: 'group', 'aria-labelledby': groupId } : {})}>
        {children}
        {hint && <div className="mt-1 break-words text-xs text-muted-foreground">{hint}</div>}
      </div>
    </div>
  );
}
